import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";

/**
 * Custom hook to synchronize schedule state (date, time, days) to URL query parameters.
 *
 * @param hydratedRef - Ref tracking whether initial hydration has completed
 *
 * @remarks
 * - Only syncs after initial hydration to avoid overwriting URL on mount
 * - Uses refs to prevent redundant URL updates
 * - Syncs pickupDate, pickupTime, and pickupDays parameters
 * - Uses replace navigation to avoid polluting browser history
 *
 * @example
 * ```tsx
 * function SchedulePage() {
 *   const hydratedRef = useRef(false);
 *   useScheduleHydration(); // sets hydratedRef
 *   useScheduleUrlSync(hydratedRef);
 * }
 * ```
 */
export function useScheduleUrlSync(hydratedRef: React.MutableRefObject<boolean>) {
  const location = useLocation();
  const navigate = useNavigate();

  const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);

  const lastWrittenDateRef = useRef<string | null>(null);
  const lastWrittenTimeRef = useRef<string | null>(null);
  const lastWrittenDaysRef = useRef<string | null>(null);

  // Sync pickupDate parameter
  useEffect(() => {
    if (!hydratedRef.current) return; // Wait for initial hydration
    if (!orderData.checkInDate) return;

    const dateStr = orderData.checkInDate.format("YYYY-MM-DD");
    if (lastWrittenDateRef.current === dateStr) return; // Prevent redundant writes

    const params = new URLSearchParams(location.search);
    const currentDate = params.get("pickupDate");

    if (currentDate === dateStr) {
      lastWrittenDateRef.current = dateStr;
      return;
    }

    params.set("pickupDate", dateStr);
    lastWrittenDateRef.current = dateStr;

    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData.checkInDate, hydratedRef, location.pathname, navigate]);

  // Sync pickupTime parameter
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!orderData.checkInDate) return;
    // Removed the hasSelectedPickupTime check - we want to sync the time even if it's a default value
    // This ensures the time is always present in the URL for proper hydration on other pages

    const timeStr = orderData.checkInDate.format("HH:mm");
    if (lastWrittenTimeRef.current === timeStr) return;

    const params = new URLSearchParams(location.search);
    const currentTime = params.get("pickupTime");

    if (currentTime === timeStr) {
      lastWrittenTimeRef.current = timeStr;
      return;
    }

    params.set("pickupTime", timeStr);
    lastWrittenTimeRef.current = timeStr;

    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderData.checkInDate,
    hydratedRef,
    location.pathname,
    navigate,
  ]);

  // Sync pickupDays parameter
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (!orderData.checkInDate || !orderData.checkOutDate) return;

    const numberOfDays = orderData.checkOutDate.diff(orderData.checkInDate, "days");
    const daysStr = numberOfDays.toString();

    if (lastWrittenDaysRef.current === daysStr) return;

    const params = new URLSearchParams(location.search);
    const currentDays = params.get("pickupDays");

    if (currentDays === daysStr) {
      lastWrittenDaysRef.current = daysStr;
      return;
    }

    params.set("pickupDays", daysStr);
    lastWrittenDaysRef.current = daysStr;

    const newSearch = `?${params.toString()}`;
    const newUrl = `${location.pathname}${newSearch}`;

    // Use both history API and navigate for immediate update
    window.history.replaceState(null, "", newUrl);
    navigate({ pathname: location.pathname, search: newSearch }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderData.checkInDate,
    orderData.checkOutDate,
    hydratedRef,
    location.pathname,
    navigate,
  ]);
}
